import { useSuspenseQuery } from '@tanstack/react-query';
import { Interval } from 'app/agriculture/AgTimeline/types';
import { Record } from 'immutable';
import { z } from 'zod';
import { snapshotConfig } from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import {
    AggregatableSnapshotType,
    aggregationSnapshotConfig,
} from 'app/agriculture/queries/snapshots/config/snapshotAggregationConfig';

export type ChartInterval = 'hour' | 'day' | 'week' | 'month' | 'year';

export function useAggregateChartData<TSnapshot extends AggregatableSnapshotType>({
    snapshotType,
    deviceId,
    intervalUnit,
    interval: { start, end },
}: {
    deviceId: string;
    snapshotType: TSnapshot;
    intervalUnit: ChartInterval;
    interval: Interval<Date>;
}) {
    const { data } = useSuspenseQuery({
        queryKey: [
            `${snapshotConfig[snapshotType].endpoint}agg/`,
            {
                device_id: deviceId,
                timestamp_start: start.toISOString(),
                timestamp_end: end.toISOString(),
                interval: intervalUnit,
            },
        ],
    });

    const schema = aggregationSnapshotConfig[snapshotType];
    return schema.array().parse(data) as z.infer<typeof schema>[];
}

export function getAggregateDataFieldRecord<T extends { timestamp?: string }>(
    data: T[],
    field: keyof T,
): Record<string, number> {
    return data.reduce<Record<string, number>>((acc, item) => {
        if (item.timestamp == null) {
            return acc;
        }
        const value = item[field];
        acc[item.timestamp] = typeof value === 'number' ? value : NaN;
        return acc;
    }, {});
}
